/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import PDFLinkCell from "@eisinc/App/Components/PaymentCenter/PDFLinkCell";
import getSystemList from "@eisinc/App/Handlers/XNG/getSystemList";
import { GetSystemListValuesModel } from "@eisinc/App/Services/SystemListService";
import {
    calcSelectedInvoicesTotal,
    formatter,
    getPaginatedInvoicePayments,
    handleSortInvoicePayments,
    handleSwitchSortArrow,
} from "@eisinc/App/Utilities/PaymentCenterUtilities";
import Checkbox from "@eisinc/MobiusOverrides/Checkbox/Checkbox";
import Pagination, { PaginationPresentationProps } from "@eisinc/MobiusOverrides/Pagination";
import Tooltip, { TooltipPresentationProps } from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import {
    COMMENT_REQUIRED_FIELD_MESSAGE,
    DEFAULT_INVOICE_PAYMENT_SORT_KEY,
    DEFAULT_PAGE_SIZE,
    InvoicePaymentAttributes,
    REASON_CODE_REQUIRED_FIELD_MESSAGE,
    SORT_INVOICE_PAYMENT_BY_FIELD,
} from "@eisinc/XDL/constants";
import { InvoicePaymentModel } from "@eisinc/XDL/interfaces";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import DataTable from "@insite/mobius/DataTable";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import {
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Select,
    TextArea,
    TextField,
    Typography,
} from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    invoicePaymentItems?: InvoicePaymentModel[];
    setSelectedInvoicePayments: (selectedInvoicePayments: InvoicePaymentModel[]) => void;
    dueDateRanges: GetSystemListValuesModel[];
}

export interface SelectInvoicesTableStyle {
    wrapper?: InjectableCss;
    tableWrapper?: InjectableCss;
    selectedRow?: DataTableRowProps;
    actionsRow?: DataTableRowProps;
    textField?: TextFieldProps;
    tableHeaderWrapper?: InjectableCss;
    headerLeftWrapper?: InjectableCss;
    headerRightWrapper?: InjectableCss;
    invoicesCount?: TypographyProps;
    invoicesLabel?: TypographyProps;
    invoicesTotal?: TypographyProps;
    pagination?: PaginationPresentationProps;
    tooltip?: TooltipPresentationProps;
    tooltipWrapper?: InjectableCss;
}

export const selectInvoicesTableStyles: SelectInvoicesTableStyle = {};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

const mapDispatchToProps = {
    getSystemList,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const SelectInvoicesTable: React.FunctionComponent<Props> = ({
    invoicePaymentItems,
    setSelectedInvoicePayments,
    dueDateRanges,
    language,
    getSystemList,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const styles = selectInvoicesTableStyles;

    const [invoicePayments, setInvoicePayments] = React.useState<InvoicePaymentModel[] | undefined>();
    const [sortOrder, setSortOrder] = React.useState(SORT_INVOICE_PAYMENT_BY_FIELD);
    const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
    const [page, setPage] = React.useState(1);
    const pageSizeOptions = [DEFAULT_PAGE_SIZE];
    const [reasonCodes, setReasonCodes] = React.useState<GetSystemListValuesModel[]>([]);
    const [showSelectedInvoices, setShowSelectedInvoices] = React.useState(false);
    const [selectedSortKey, setSelectedSortKey] = React.useState<keyof InvoicePaymentModel>(
        DEFAULT_INVOICE_PAYMENT_SORT_KEY,
    );

    const handleSortInvoicePaymentsEvent = (sortField: keyof InvoicePaymentModel) => {
        setSelectedSortKey(sortField);
        const updateSortOrder = handleSwitchSortArrow(sortField, sortOrder);
        setSortOrder(updateSortOrder);
        const sortedInvoicePayments = handleSortInvoicePayments(invoicePayments, sortField, updateSortOrder);
        setInvoicePayments(sortedInvoicePayments);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        if (!newPageSize) {
            setPage(1);
            setPageSize(1000);
        } else {
            setPageSize(newPageSize);
        }
    };

    const getFormattedRanges = () => {
        const formattedRanges = dueDateRanges?.map(range => {
            if (range && range.name) {
                if (typeof range.name === "string") {
                    const formattedName = range.name.replace("Infinity", Number.MAX_SAFE_INTEGER.toString());
                    range.name = JSON.parse(formattedName);
                    return range;
                }
                return range;
            }
            return null;
        });

        return formattedRanges;
    };

    const filterInvoicesByDueDate = () => {
        const formattedRanges = getFormattedRanges() as GetSystemListValuesModel[];
        const newInvoicePayments = [...(invoicePayments as any)];

        newInvoicePayments.forEach(invoice => {
            const dueDatePast = invoice.dueDatePast;
            let isSelected = false;
            formattedRanges?.forEach(range => {
                if (
                    range.isChecked &&
                    dueDatePast >= range.name[0] &&
                    dueDatePast <= range.name[1] &&
                    !invoice.hideCheckBox
                ) {
                    isSelected = true;
                }
            });
            invoice.selected = isSelected;
        });

        setInvoicePayments(newInvoicePayments);
    };

    const loadReasonCodes = () => {
        getSystemList({
            systemListName: "Custom_InvoicePaymentShortPayReasonCodes",
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    setReasonCodes(resultProps.apiResult.result);
                }
            },
        });
    };

    React.useEffect(() => {
        if (invoicePaymentItems) {
            setInvoicePayments(invoicePaymentItems?.filter(invoice => invoice.invoiceAmount > 0));
        }
    }, [invoicePaymentItems]);

    React.useEffect(() => {
        if (dueDateRanges?.length && invoicePayments?.length) {
            filterInvoicesByDueDate();
        }
    }, [dueDateRanges]);

    React.useEffect(() => {
        loadReasonCodes();
    }, []);

    const paginatedInvoicePayment = React.useMemo(() => {
        if (showSelectedInvoices) {
            const SelectedInvoicePayments = invoicePayments?.filter(invoice => invoice.selected === true);
            return getPaginatedInvoicePayments(
                page,
                pageSize,
                handleSortInvoicePayments(SelectedInvoicePayments, selectedSortKey, sortOrder),
            );
        }
        return getPaginatedInvoicePayments(
            page,
            pageSize,
            handleSortInvoicePayments(invoicePayments, selectedSortKey, sortOrder),
        );
    }, [invoicePayments, page, pageSize, showSelectedInvoices, sortOrder]);

    const rows = paginatedInvoicePayment?.map(invoice => {
        return {
            id: invoice.invoiceItemNumber,
            invoiceItemNumber: invoice.invoiceItemNumber,
            invoiceNumber: invoice.invoiceNumber,
            po: invoice.purchaseOrderNumber,
            invoiceDate: invoice.invoiceDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.invoiceDate),
                      language,
                  })
                : "",
            sapFiDocumentType: invoice.sapFiDocumentType,
            itemText: invoice.itemText,
            calculatedDueDate: invoice.calculatedDueDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.calculatedDueDate),
                      language,
                  })
                : "",
            invoiceAmount: invoice.invoiceAmount.toFixed(2),
            invoiceAmountDisplay: invoice.invoiceAmountDisplay,
            dueDatePast: invoice.dueDatePast,
            selected: invoice.selected,
            amountToPay: invoice.amountToPay || invoice.invoiceAmount,
            additionalComments: invoice.additionalComments || "",
            reasonForReducedPrice: invoice.reasonForReducedPrice || "",
            shouldShowOtherActions: invoice.shouldShowOtherActions || false,
            additionalCommentsError: invoice.additionalCommentsError || "",
            reasonForReducedPriceError: invoice.reasonForReducedPriceError || "",
            isPaid: invoice.hideCheckBox,
            originalInvoiceNumber: invoice.originalInvoiceNumber,
        } as any;
    });

    const selectedInvoices = invoicePayments?.filter(invoice => invoice.selected === true);
    setSelectedInvoicePayments(selectedInvoices || []);

    const selectChangeHandler = (invoiceItemNumber: string, value: boolean) => {
        // Update invoice line checkbox
        const newInvoicePayments = [...(invoicePayments as any)];
        const index = newInvoicePayments?.findIndex(invoice => invoice.invoiceItemNumber === invoiceItemNumber);

        newInvoicePayments[index].selected = value;
        if (!value) {
            newInvoicePayments[index].selected = value;
            newInvoicePayments[index].amountToPay = null;
            newInvoicePayments[index].reasonForReducedPrice = "";
            newInvoicePayments[index].additionalComments = "";
            newInvoicePayments[index].shouldShowOtherActions = false;
        } else {
            newInvoicePayments[index].amountToPay =
                newInvoicePayments[index].amountToPay?.toFixed?.(2) ||
                newInvoicePayments[index].invoiceAmount?.toFixed(2);
        }
        setInvoicePayments(newInvoicePayments);

        // Update Date Range checkboxes
        const formattedRanges = getFormattedRanges() as GetSystemListValuesModel[];
        formattedRanges?.forEach(range => {
            const invoicesInRange = newInvoicePayments.filter(invoice => {
                const dueDatePast = invoice.dueDatePast;
                return dueDatePast >= range.name[0] && dueDatePast <= range.name[1];
            });

            const allInvoicesSelected = invoicesInRange?.length && invoicesInRange.every(invoice => invoice.selected);
            // TODO: remove allInvoicesDeselected if it is not needed
            const allInvoicesDeselected =
                invoicesInRange?.length && invoicesInRange.every(invoice => !invoice.selected);

            if (range.isChecked && !allInvoicesSelected) {
                range.isChecked = false;
            } else if (!range.isChecked && allInvoicesSelected) {
                range.isChecked = true;
            }
        });
    };

    const handleAmountToPayChange = (e: any, invoiceItemNumber: string, invoiceAmount: number) => {
        const inputValue = e.target.value;
        if (/^\d*\.?\d{0,2}$/.test(inputValue) || inputValue === "") {
            const newInvoicePayments = [...(invoicePayments as any)];
            const index = newInvoicePayments?.findIndex(invoice => invoice.invoiceItemNumber === invoiceItemNumber);
            newInvoicePayments[index].amountToPay = inputValue;
            setInvoicePayments(newInvoicePayments);
        }
    };

    const handleAmountToPayBlur = (e: any, invoiceItemNumber: string, invoiceAmount: number) => {
        const inputValue = e.target.value;
        if (/^\d*\.?\d{0,2}$/.test(inputValue) || inputValue === "") {
            const newInvoicePayments = [...(invoicePayments as any)];
            const index = newInvoicePayments?.findIndex(invoice => invoice.invoiceItemNumber === invoiceItemNumber);

            if (parseFloat(inputValue) >= invoiceAmount) {
                newInvoicePayments[index].amountToPay = invoiceAmount;
                newInvoicePayments[index].shouldShowOtherActions = false;
                newInvoicePayments[index].reasonForReducedPrice = "";
                newInvoicePayments[index].additionalComments = "";
                setInvoicePayments(newInvoicePayments);
            }
            if (parseFloat(inputValue) < invoiceAmount) {
                newInvoicePayments[index].amountToPay = Number(newInvoicePayments[index].amountToPay)?.toFixed(2);
                newInvoicePayments[index].shouldShowOtherActions = true;
                setInvoicePayments(newInvoicePayments);
            }
        }
    };

    const handleReasonForReducePriceChange = (event: any, invoiceItemNumber: string) => {
        const value = event.target.value;
        const newInvoicePayments = [...(invoicePayments as any)];
        const index = newInvoicePayments?.findIndex(invoice => invoice.invoiceItemNumber === invoiceItemNumber);

        if (value || value === "") {
            newInvoicePayments[index].reasonForReducedPrice = value;
            newInvoicePayments[index].reasonForReducedPriceError =
                value === "" ? REASON_CODE_REQUIRED_FIELD_MESSAGE : "";
            setInvoicePayments(newInvoicePayments);
        } else {
            newInvoicePayments[index].reasonForReducedPriceError = REASON_CODE_REQUIRED_FIELD_MESSAGE;
            setInvoicePayments(newInvoicePayments);
        }
    };

    const handleAdditionalCommentsChange = (event: any, invoiceItemNumber: string) => {
        const value = event.target.value;
        const newInvoicePayments = [...(invoicePayments as any)];
        const index = newInvoicePayments?.findIndex(invoice => invoice.invoiceItemNumber === invoiceItemNumber);

        newInvoicePayments[index].additionalComments = value;

        if (!value?.trim()) {
            newInvoicePayments[index].additionalCommentsError = COMMENT_REQUIRED_FIELD_MESSAGE;
        } else {
            newInvoicePayments[index].additionalCommentsError = "";
        }

        setInvoicePayments(newInvoicePayments);
    };

    const handleAdditionalCommentsBlur = (event: any, invoiceItemNumber: string) => {
        const value = event.target.value;
        const newInvoicePayments = [...(invoicePayments as any)];
        const index = newInvoicePayments?.findIndex(invoice => invoice.invoiceItemNumber === invoiceItemNumber);

        if (!value) {
            newInvoicePayments[index].additionalCommentsError = COMMENT_REQUIRED_FIELD_MESSAGE;
        } else {
            newInvoicePayments[index].additionalCommentsError = "";
        }

        setInvoicePayments(newInvoicePayments);
    };

    const handleShowSelectedInvoicesChanged = (_: React.SyntheticEvent, value: boolean) => {
        setShowSelectedInvoices(value);
    };

    const renderTableHeader = () => (
        <StyledWrapper {...styles.tableHeaderWrapper}>
            <StyledWrapper {...styles.headerLeftWrapper}>
                <Typography {...styles.invoicesCount}>{selectedInvoices?.length}</Typography>
                <Typography {...styles.invoicesLabel}>{translate("Invoice(s) Selected:")}</Typography>
                <Typography {...styles.invoicesTotal}>
                    {formatter.format(calcSelectedInvoicesTotal(selectedInvoices || []))}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.headerRightWrapper}>
                <Checkbox
                    checked={showSelectedInvoices}
                    onChange={handleShowSelectedInvoicesChanged}
                    variant="toggle"
                    sizeVariant="small"
                    secondaryLabel={translate("Show Selected Invoices")}
                >
                    {translate("Show All Invoices")}
                </Checkbox>
            </StyledWrapper>
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            {renderTableHeader()}
            <StyledWrapper {...styles.tableWrapper}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader
                            sorted={sortOrder.originalInvoiceNumber}
                            onSortClick={() =>
                                handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.originalInvoiceNumber)
                            }
                            data-test-selector="tableHeader_originalInvoiceNumber"
                        >
                            {translate("IPInvoice")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.invoiceItemNumber}
                            onSortClick={() =>
                                handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceItemNumber)
                            }
                            data-test-selector="tableHeader_invoiceItemNumber"
                        >
                            {translate("IPDOC")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.purchaseOrderNumber}
                            onSortClick={() =>
                                handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.purchaseOrderNumber)
                            }
                            data-test-selector="tableHeader_purchaseOrderNumber"
                        >
                            {translate("PO #")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.invoiceDate}
                            onSortClick={() => handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceDate)}
                            data-test-selector="tableHeader_invoiceDate"
                        >
                            {translate("Doc. Date")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.calculatedDueDate}
                            onSortClick={() =>
                                handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.calculatedDueDate)
                            }
                            data-test-selector="tableHeader_calculatedDueDate"
                        >
                            {translate("Due Date")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.invoiceAmount}
                            onSortClick={() => handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceAmount)}
                            data-test-selector="tableHeader_invoiceAmount"
                        >
                            {translate("Amount")}
                        </DataTableHeader>
                        <DataTableHeader data-test-selector="tableHeader_invoiceActions">
                            {translate("Actions")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="invoicePaymentTable_tableBody">
                        {rows?.map(
                            (
                                {
                                    id,
                                    invoiceItemNumber,
                                    po,
                                    invoiceDate,
                                    calculatedDueDate,
                                    invoiceAmount,
                                    invoiceAmountDisplay,
                                    selected,
                                    amountToPay,
                                    additionalComments,
                                    reasonForReducedPrice,
                                    shouldShowOtherActions,
                                    additionalCommentsError,
                                    reasonForReducedPriceError,
                                    isPaid,
                                    itemText,
                                    invoiceNumber,
                                    originalInvoiceNumber,
                                },
                                index,
                            ) => (
                                <React.Fragment key={id}>
                                    <DataTableRow
                                        {...styles.selectedRow}
                                        className={`${selected ? "selected" : ""} ${isPaid ? "paid" : ""}`}
                                    >
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_originalInvoiceNumber">
                                            <StyledWrapper {...styles.tooltipWrapper}>
                                                <PDFLinkCell invoice={rows[index]} showOriginalInvoiceNumber />
                                                {originalInvoiceNumber && (
                                                    <Tooltip {...styles.tooltip} text={itemText} />
                                                )}
                                            </StyledWrapper>
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_invoiceNumber">
                                            {invoiceNumber}
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_purchaseNumber">
                                            {po}
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_invoiceDate">
                                            {invoiceDate}
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_calculatedDueDate">
                                            {calculatedDueDate}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_invoiceAmount"
                                            {...{ isCredit: invoiceAmount < 0 }}
                                        >
                                            {invoiceAmountDisplay}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_invoiceActions"
                                            className={isPaid ? "pending" : "actions"}
                                        >
                                            {isPaid ? (
                                                <Typography as="span">{translate("Pending")}</Typography>
                                            ) : (
                                                <Checkbox
                                                    uid={id}
                                                    onChange={(e: any, value: boolean) => {
                                                        selectChangeHandler(invoiceItemNumber, value);
                                                    }}
                                                    checked={selected}
                                                >
                                                    {translate("Pay Invoice")}
                                                </Checkbox>
                                            )}
                                        </DataTableCell>
                                    </DataTableRow>
                                    {selected && (
                                        <DataTableRow
                                            {...styles.actionsRow}
                                            {...{
                                                highlighted:
                                                    shouldShowOtherActions &&
                                                    (reasonForReducedPrice === "" || additionalComments === ""),
                                            }}
                                        >
                                            <DataTableCell colSpan={2}>
                                                {shouldShowOtherActions && (
                                                    <Select
                                                        label={translate("Reason for Reduced Price")}
                                                        value={reasonForReducedPrice}
                                                        disabled={false}
                                                        required={true}
                                                        onChange={(e: any) => {
                                                            handleReasonForReducePriceChange(e, invoiceItemNumber);
                                                        }}
                                                        error={reasonForReducedPriceError}
                                                    >
                                                        <option value="">{translate("Select Reason")}</option>
                                                        {reasonCodes.map(code => (
                                                            <option key={code.name} value={code.name}>
                                                                {code.description}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                )}
                                            </DataTableCell>
                                            <DataTableCell colSpan={3}>
                                                {shouldShowOtherActions && (
                                                    <TextArea
                                                        label={translate("Additional Comments")}
                                                        required={true}
                                                        onChange={(e: any) => {
                                                            handleAdditionalCommentsChange(e, invoiceItemNumber);
                                                        }}
                                                        onBlur={(e: any) => {
                                                            handleAdditionalCommentsBlur(e, invoiceItemNumber);
                                                        }}
                                                        error={additionalCommentsError}
                                                        value={additionalComments}
                                                        maxLength={4000}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell colSpan={2}>
                                                <TextField
                                                    id={`paymentOnAccount_${index}`}
                                                    label={translate("Amount to Pay")}
                                                    {...styles.textField}
                                                    onChange={(e: any) => {
                                                        handleAmountToPayChange(e, invoiceItemNumber, invoiceAmount);
                                                    }}
                                                    onBlur={(e: any) => {
                                                        handleAmountToPayBlur(e, invoiceItemNumber, invoiceAmount);
                                                    }}
                                                    value={amountToPay}
                                                    maxLength={15}
                                                />
                                            </DataTableCell>
                                        </DataTableRow>
                                    )}
                                </React.Fragment>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            <StyledWrapper>
                <Pagination
                    resultsCount={invoicePayments?.length || 0}
                    currentPage={page}
                    resultsPerPage={pageSize}
                    resultsPerPageLabel={translate("Per Page")}
                    resultsPerPageOptions={pageSizeOptions}
                    onChangeResultsPerPage={changeResultsPerPage}
                    onChangePage={(newPageIndex: number) => setPage(newPageIndex || 1)}
                    withAllOption
                    {...styles.pagination}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectInvoicesTable);
